import { Download, Loader2, X } from "lucide-react";
import type { ExportCapabilities } from "@/types";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface SelectionOption {
  id: string;
  label: string;
  desc: string;
}

/** A single selectable card button used in option grids. */
function SelectionCard({
  option,
  isSelected,
  isDisabled,
  note,
  onClick,
}: Readonly<{
  option: SelectionOption;
  isSelected: boolean;
  isDisabled?: boolean;
  note?: string | null;
  onClick: () => void;
}>) {
  const className = (() => {
    if (isSelected) return "border-brand-primary bg-brand-primary-subtle text-content-primary";
    if (isDisabled) return "border-border-primary bg-surface-secondary/50 text-content-quaternary cursor-not-allowed";
    return "border-border-secondary bg-surface-secondary text-content-secondary hover:border-border-hover";
  })();

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`p-3 rounded-lg border text-left transition ${className}`}
    >
      <div className="font-medium text-sm">{option.label}</div>
      <div className="text-xs mt-0.5 opacity-70">{option.desc}</div>
      {isDisabled && note && <div className="text-xs mt-1 text-amber-500">{note}</div>}
    </button>
  );
}

/** A labeled grid of selectable option cards. */
function OptionGroup({
  label,
  options,
  selected,
  columns = 2,
  capabilities,
  onSelect,
}: Readonly<{
  label: string;
  options: SelectionOption[];
  selected: string;
  columns?: number;
  capabilities?: ExportCapabilities | null;
  onSelect: (id: string) => void;
}>) {
  return (
    <div>
      <span className="block text-sm font-medium text-content-secondary mb-2">{label}</span>
      <div className={`grid gap-2 ${columns === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {options.map((opt) => {
          const capInfo = capabilities?.formats.find((f) => f.id === opt.id);
          const isDisabled = capInfo ? !capInfo.available : false;

          return (
            <SelectionCard
              key={opt.id}
              option={opt}
              isSelected={selected === opt.id}
              isDisabled={isDisabled}
              note={capInfo?.note}
              onClick={() => !isDisabled && onSelect(opt.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Toggle switch for boolean options. */
function ToggleSwitch({
  label,
  checked,
  onChange,
}: Readonly<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}>) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-content-secondary">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-6 rounded-full transition ${checked ? "bg-brand-primary" : "bg-surface-tertiary"}`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${checked ? "translate-x-4" : ""}`}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Props mirror the return value of useChatExport, minus `openExportModal`
 * which is used by the caller to toggle visibility.
 */
interface ExportModalProps {
  isExportModalOpen: boolean;
  exportFormat: string;
  exportTemplate: string;
  exportContent: string;
  exportIncludeSystem: boolean;
  isExporting: boolean;
  exportCapabilities: ExportCapabilities | null;
  exportError: string | null;
  setExportFormat: (v: string) => void;
  setExportTemplate: (v: string) => void;
  setExportContent: (v: string) => void;
  setExportIncludeSystem: (v: boolean) => void;
  closeExportModal: () => void;
  handleExport: () => void;
}

/**
 * Modal for exporting a chat session to DOCX / PDF / Markdown.
 * Extracted from Chat.tsx to reduce the monolith.
 */
export function ExportModal(props: Readonly<ExportModalProps>) {
  if (!props.isExportModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-primary">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-brand-primary" />
            <h3 className="text-lg font-semibold text-content-primary">Export Chat</h3>
          </div>
          <button onClick={props.closeExportModal} className="p-1.5 hover:bg-interactive-hover rounded-lg transition">
            <X className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          <OptionGroup
            label="Format"
            options={[
              { id: "docx", label: "DOCX", desc: "Word Document" },
              { id: "pdf", label: "PDF", desc: "PDF Document" },
              { id: "md", label: "Markdown", desc: "Plain Text" },
            ]}
            selected={props.exportFormat}
            columns={3}
            capabilities={props.exportCapabilities}
            onSelect={props.setExportFormat}
          />

          {/* Template selection - only for DOCX and PDF */}
          {(props.exportFormat === "docx" || props.exportFormat === "pdf") && (
            <OptionGroup
              label="Template"
              options={[
                { id: "clean", label: "Clean Minimal", desc: "Simple black & white" },
                { id: "branded", label: "Company Branded", desc: "Your logo & brand colors" },
              ]}
              selected={props.exportTemplate}
              onSelect={props.setExportTemplate}
            />
          )}

          <OptionGroup
            label="Content"
            options={[
              { id: "full", label: "Full Conversation", desc: "Complete chat history" },
              { id: "summary", label: "Summary", desc: "AI-generated overview" },
            ]}
            selected={props.exportContent}
            onSelect={props.setExportContent}
          />

          <ToggleSwitch
            label="Include system messages"
            checked={props.exportIncludeSystem}
            onChange={props.setExportIncludeSystem}
          />

          {/* Error message */}
          {props.exportError && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              {props.exportError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border-primary">
          <button
            onClick={props.closeExportModal}
            className="px-4 py-2 text-sm text-content-tertiary hover:text-content-primary transition"
          >
            Cancel
          </button>
          <button
            onClick={props.handleExport}
            disabled={props.isExporting}
            className="flex items-center gap-2 px-5 py-2 bg-brand-gradient hover:opacity-90 text-white rounded-lg transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {props.isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
